/**
 * Psixrometriya — nam havo xossalari (havo–suv bug'i aralashmasi).
 *
 * MANBA: Kasatkin A.G., 15-bob "Quritish" (I–x diagramma tenglamalari);
 *        Pavlov, Romankov, Noskov, "Quritish" bo'limi;
 *        Perry's Chemical Engineers' Handbook, 8th ed., Sec. 12 (Psychrometry);
 *        Treybal, Mass-Transfer Operations, Ch. 7 (adiabatik to'yinish, nam termometr).
 *
 * TENGLAMALAR (Y — namlik miqdori, kg suv / kg quruq havo; Ps — suv bug' bosimi, Antoine):
 *   Y      = (M_w/M_a) · φ·Ps(T) / (P − φ·Ps(T)),     M_w/M_a = 0.622
 *   φ      = Y·P / ((M_w/M_a + Y) · Ps(T))
 *   I      = cp_a·(T − 273.15) + Y·(r0 + cp_v·(T − 273.15))       [J/kg quruq havo]
 *              cp_a = 1005 J/(kg·K)  (quruq havo, 0…100 °C, Treybal/Perry's)
 *              cp_v = 1970 J/(kg·K)  (suv bug'i, Kasatkin: 1.97 kJ/(kg·K))
 *              r0   = 2501 kJ/kg     (suvning 0 °C dagi bug'lanish issiqligi, Perry's;
 *                                     Kasatkin 2493 kJ/kg ishlatadi — farq 0.3%)
 *   T(I,Y) = 273.15 + (I − Y·r0)/(cp_a + Y·cp_v)      (analitik teskari)
 *   Shudring nuqtasi: Ps(T_dp) = p_v = Y·P/(M_w/M_a + Y)  →  T_dp = saturationTemperature('water', p_v)
 *   Nam termometr (adiabatik to'yinish): I(T_wb, Y_sat(T_wb)) = I(T, Y), bisection [T_dp, T]
 *   Solishtirma hajm: v = R·T·(1/M_a + Y/M_w)/P  [m³/kg quruq havo]
 *   Zichlik: ρ = (1 + Y)/v  [kg nam havo / m³]
 *
 * ASSUMPTION:
 *   1. Ideal gaz aralashmasi (P < ~2 bar), Dalton qonuni.
 *   2. cp_a, cp_v, r0 o'zgarmas — I–x diagrammaning standart chiziqlashtirilishi (Kasatkin).
 *      Shu tufayli T(I, Y) analitik va adiabatik quritgichda I2 = I1 aynan bajariladi.
 *   3. Nam termometr harorati = adiabatik to'yinish harorati (havo–suv uchun Lewis soni ≈ 1,
 *      Treybal 7-bob). Boshqa suyuqliklar uchun bu to'g'ri emas.
 *   4. To'yinish bosimi Antoine (Lange, 1…100 °C); 0 °C dan past yoki 100 °C dan yuqori
 *      haroratda extrapolyatsiya (properties `property.outOfRange` info ogohlantirishi).
 *   5. Suvning entalpiyasi tarkibida suyuq holatdagi issiqlik (c_w·θ) yo'q — I faqat havo + bug'.
 */
import { getSubstance, R_GAS, saturationPressure, saturationTemperature } from '../../properties';
import { bisection } from '../../solvers/bisection';
import type { WarningSink } from '../../types';

export const CP_DRY_AIR = 1005; // J/(kg·K)
export const CP_VAPOR = 1970; // J/(kg·K)
export const R0_WATER = 2501e3; // J/kg, 0 °C
const T_REF = 273.15;

// Molyar massalar lazy: moddalar pack yuklangandan keyin mavjud bo'ladi
const M_W = (): number => getSubstance('water').molarMass;
const M_A = (): number => getSubstance('air').molarMass;
/** M_w/M_a ≈ 0.622 */
export const HUMIDITY_RATIO_CONST = (): number => M_W() / M_A();

/** Suv bug' bosimi Ps(T), Pa */
export function waterVaporPressure(T: number, warnings?: WarningSink): number {
  return saturationPressure('water', T, warnings);
}

/**
 * Namlik miqdori Y(T, φ, P). φ·Ps ≥ P bo'lsa (fizik jihatdan bo'lmaydi — suv qaynaydi)
 * Infinity qaytariladi; chaqiruvchi tekshiradi.
 */
export function humidityRatio(T: number, phi: number, P: number, warnings?: WarningSink): number {
  const pv = phi * waterVaporPressure(T, warnings);
  if (pv >= P) return Infinity;
  return (HUMIDITY_RATIO_CONST() * pv) / (P - pv);
}

/** To'yingan havo namlik miqdori Y_sat(T, P) */
export function saturationHumidity(T: number, P: number, warnings?: WarningSink): number {
  return humidityRatio(T, 1, P, warnings);
}

/** Bug'ning parsial bosimi p_v(Y, P), Pa */
export function vaporPartialPressure(Y: number, P: number): number {
  return (Y * P) / (HUMIDITY_RATIO_CONST() + Y);
}

/** Nisbiy namlik φ(T, Y, P). 1 dan katta qiymat "o'ta to'yingan" (fizik emas) degani. */
export function relativeHumidity(T: number, Y: number, P: number, warnings?: WarningSink): number {
  return vaporPartialPressure(Y, P) / waterVaporPressure(T, warnings);
}

/** Nam havo entalpiyasi I(T, Y), J/kg quruq havo */
export function humidEnthalpy(T: number, Y: number): number {
  const t = T - T_REF;
  return CP_DRY_AIR * t + Y * (R0_WATER + CP_VAPOR * t);
}

/** Entalpiya va namlikdan harorat (analitik teskari), K */
export function temperatureFromEnthalpy(I: number, Y: number): number {
  return T_REF + (I - Y * R0_WATER) / (CP_DRY_AIR + Y * CP_VAPOR);
}

/** Shudring nuqtasi T_dp(Y, P), K. Y ≤ 0 → NaN. */
export function dewPoint(Y: number, P: number, warnings?: WarningSink): number {
  if (!(Y > 0)) return NaN;
  return saturationTemperature('water', vaporPartialPressure(Y, P), warnings);
}

export interface WetBulbResult {
  /** K */
  T: number;
  iterations: number;
  converged: boolean;
}

/**
 * Nam termometr (adiabatik to'yinish) harorati: I(T_wb, Y_sat(T_wb)) = I(T, Y).
 * Ildiz [T_dp, T] oralig'ida (Y to'yingan bo'lsa T_wb = T).
 */
export function wetBulbTemperature(
  T: number,
  Y: number,
  P: number,
  warnings?: WarningSink,
): WetBulbResult {
  const I = humidEnthalpy(T, Y);
  if (Y >= saturationHumidity(T, P, warnings)) return { T, iterations: 0, converged: true };
  // Quyi chegara: shudring nuqtasi (Y > 0) yoki 200 K (juda quruq havo).
  // Yuqori chegara: T, lekin qaynash haroratidan past (Y_sat → ∞ da).
  const lo = Y > 0 ? Math.max(dewPoint(Y, P), 200) : 200;
  const hi = Math.min(T, saturationTemperature('water', P) - 1e-6);
  const f = (Tw: number) => humidEnthalpy(Tw, saturationHumidity(Tw, P)) - I;
  const r = bisection(f, lo, hi, { xtol: 1e-9, ftol: 1e-6 });
  return { T: r.value, iterations: r.iterations, converged: r.converged };
}

/** Solishtirma hajm, m³ / kg quruq havo */
export function specificVolume(T: number, Y: number, P: number): number {
  return (R_GAS * T * (1 / M_A() + Y / M_W())) / P;
}

/** Nam havo zichligi (nam havo massasi / hajm), kg/m³ */
export function humidAirDensity(T: number, Y: number, P: number): number {
  return (1 + Y) / specificVolume(T, Y, P);
}
